import { FileEditor, FileTree, toast } from '@avila-roffe/components-web';
import type { ChangeEvent } from 'react';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import {
  buildFileNodes,
  childPath,
  collectFolderPaths,
  renamedPath,
  upsertLocalFile,
} from '../fileTreeUtils';
import { useSkillFiles } from '../hooks/use-skills';
import { deleteSkillFile, upsertSkillFile } from '../skillApi';
import type { SkillFile } from '../types';

type AutoSaveState = 'idle' | 'saving' | 'saved' | 'error';

export interface SkillFileEditorHandle {
  cancelEdit: () => void;
}

interface SkillFileEditorProps {
  appSlug: string;
  name: string;
  onEditingChange: (editing: boolean, autoSaveState: AutoSaveState) => void;
}

export const SkillFileEditor = forwardRef<SkillFileEditorHandle, SkillFileEditorProps>(
  function SkillFileEditor({ appSlug, name, onEditingChange }, ref) {
    const {
      data: queryFiles = [],
      isLoading: filesLoading,
      isError: filesError,
    } = useSkillFiles(appSlug, name);

    const [files, setFiles] = useState<SkillFile[]>([]);
    const [localFolders, setLocalFolders] = useState<Set<string>>(new Set());
    const [selectedPath, setSelectedPath] = useState<string | null>(null);
    const [editorContent, setEditorContent] = useState('');
    const [autoSaveState, setAutoSaveState] = useState<AutoSaveState>('idle');
    const [editingFile, setEditingFile] = useState(false);
    const [treeOpen, setTreeOpen] = useState(true);

    useEffect(() => {
      if (queryFiles.length > 0 && files.length === 0) {
        setFiles(queryFiles);
        setLocalFolders(collectFolderPaths(queryFiles));
        const preferred = queryFiles.find((f) => f.path === 'SKILL.md')?.path;
        setSelectedPath(preferred ?? queryFiles[0]?.path ?? null);
      }
    }, [queryFiles, files.length]);

    const selectedFile = useMemo(
      () => files.find((f) => f.path === selectedPath) ?? null,
      [files, selectedPath],
    );

    useEffect(() => {
      setEditorContent(selectedFile?.content ?? '');
      setAutoSaveState('idle');
      setEditingFile(false);
    }, [selectedFile?.content]);

    useEffect(() => {
      onEditingChange(editingFile, autoSaveState);
    }, [editingFile, autoSaveState, onEditingChange]);

    // Auto-save on edit
    useEffect(() => {
      if (!appSlug || !name || !selectedFile || selectedPath !== selectedFile.path) return;
      if (!editingFile) return;
      if (editorContent === selectedFile.content) return;

      const timer = window.setTimeout(async () => {
        try {
          setAutoSaveState('saving');
          const saved = await upsertSkillFile(appSlug, name, selectedFile.path, editorContent);
          setFiles((cur) => upsertLocalFile(cur, saved));
          setAutoSaveState('saved');
        } catch (saveError) {
          setAutoSaveState('error');
          toast.error(saveError instanceof Error ? saveError.message : 'Failed to save file');
        }
      }, 800);

      return () => window.clearTimeout(timer);
    }, [appSlug, editingFile, editorContent, name, selectedFile, selectedPath]);

    useImperativeHandle(ref, () => ({
      cancelEdit: () => {
        setEditorContent(selectedFile?.content ?? '');
        setEditingFile(false);
        setAutoSaveState('idle');
      },
    }));

    const fileNodes = useMemo(
      () => buildFileNodes(files, localFolders, selectedPath),
      [files, localFolders, selectedPath],
    );

    const onCreate = async (parent: string, type: 'file' | 'folder', nodeName: string) => {
      const path = childPath(parent, nodeName);
      if (type === 'folder') {
        setLocalFolders((cur) => {
          const next = new Set(cur);
          next.add(path);
          return next;
        });
        return;
      }
      try {
        const created = await upsertSkillFile(appSlug, name, path, '');
        setFiles((cur) => upsertLocalFile(cur, created));
        setLocalFolders((cur) => {
          const next = new Set(cur);
          for (const f of collectFolderPaths([created])) next.add(f);
          return next;
        });
        setSelectedPath(path);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to create file');
      }
    };

    const onRename = async (path: string, newNodeName: string) => {
      const node =
        fileNodes.find((e) => e.path === path) ??
        (() => {
          const stack = [...fileNodes];
          while (stack.length > 0) {
            const cur = stack.pop();
            if (!cur) continue;
            if (cur.path === path) return cur;
            if (cur.type === 'folder') stack.push(...cur.children);
          }
          return null;
        })();
      if (!node) return;

      if (node.type === 'file') {
        const nextPath = renamedPath(path, newNodeName);
        const original = files.find((f) => f.path === path);
        if (!original || nextPath === path) return;
        try {
          const saved = await upsertSkillFile(appSlug, name, nextPath, original.content);
          await deleteSkillFile(appSlug, name, path);
          setFiles((cur) => cur.filter((f) => f.path !== path).concat(saved));
          if (selectedPath === path) setSelectedPath(nextPath);
        } catch (err) {
          toast.error(err instanceof Error ? err.message : 'Failed to rename file');
        }
        return;
      }

      const nextFolderPath = renamedPath(path, newNodeName);
      if (nextFolderPath === path) return;
      const folderPrefix = `${path}/`;
      const filesToRename = files.filter((f) => f.path.startsWith(folderPrefix));
      try {
        const rewritten = await Promise.all(
          filesToRename.map((f) =>
            upsertSkillFile(
              appSlug,
              name,
              `${nextFolderPath}${f.path.slice(path.length)}`,
              f.content,
            ),
          ),
        );
        await Promise.all(filesToRename.map((f) => deleteSkillFile(appSlug, name, f.path)));
        setFiles((cur) => cur.filter((f) => !f.path.startsWith(folderPrefix)).concat(rewritten));
        setLocalFolders((cur) => {
          const next = new Set<string>();
          for (const folder of cur) {
            next.add(
              folder === path || folder.startsWith(`${path}/`)
                ? `${nextFolderPath}${folder.slice(path.length)}`
                : folder,
            );
          }
          return next;
        });
        if (selectedPath?.startsWith(folderPrefix))
          setSelectedPath(`${nextFolderPath}${selectedPath.slice(path.length)}`);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to rename folder');
      }
    };

    const onDelete = async (path: string) => {
      const file = files.find((item) => item.path === path);
      try {
        if (file) {
          await deleteSkillFile(appSlug, name, path);
          setFiles((cur) => cur.filter((item) => item.path !== path));
          if (selectedPath === path)
            setSelectedPath(files.filter((item) => item.path !== path)[0]?.path ?? null);
          return;
        }
        const folderPrefix = `${path}/`;
        const targets = files.filter((item) => item.path.startsWith(folderPrefix));
        await Promise.all(targets.map((item) => deleteSkillFile(appSlug, name, item.path)));
        setFiles((cur) => cur.filter((item) => !item.path.startsWith(folderPrefix)));
        setLocalFolders((cur) => {
          const next = new Set<string>();
          for (const folder of cur) {
            if (folder !== path && !folder.startsWith(folderPrefix)) next.add(folder);
          }
          return next;
        });
        if (selectedPath?.startsWith(folderPrefix))
          setSelectedPath(
            files.filter((item) => !item.path.startsWith(folderPrefix))[0]?.path ?? null,
          );
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to delete path');
      }
    };

    const uploadRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);

    async function handleUpload(e: ChangeEvent<HTMLInputElement>) {
      const file = e.target.files?.[0];
      if (!file) return;
      e.target.value = '';
      const path = `refs/${file.name}`;
      try {
        setUploading(true);
        const content = await file.text();
        const saved = await upsertSkillFile(appSlug, name, path, content);
        setFiles((cur) => upsertLocalFile(cur, saved));
        setLocalFolders((cur) => {
          const next = new Set(cur);
          for (const f of collectFolderPaths([saved])) next.add(f);
          return next;
        });
        setSelectedPath(path);
        toast.success(`Uploaded ${file.name} to refs/`);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Upload failed');
      } finally {
        setUploading(false);
      }
    }

    if (filesLoading) {
      return (
        <div className="flex h-[300px] items-center justify-center text-sm text-muted-foreground">
          Loading files…
        </div>
      );
    }

    if (filesError) {
      return (
        <div className="flex h-[300px] items-center justify-center text-sm text-destructive">
          Failed to load skill files.
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <input
          ref={uploadRef}
          type="file"
          className="hidden"
          onChange={(e) => void handleUpload(e)}
          aria-label="Upload file to refs folder"
        />
        <FileTree
          nodes={fileNodes}
          onSelect={(path) => setSelectedPath(path)}
          onCreate={(parent, type, nodeName) => void onCreate(parent, type, nodeName)}
          onRename={(path, nodeName) => void onRename(path, nodeName)}
          onDelete={(path) => void onDelete(path)}
          onUpload={() => uploadRef.current?.click()}
          uploadPending={uploading}
          className="rounded-2xl"
          collapsed={!treeOpen}
          onCollapseToggle={() => setTreeOpen((v) => !v)}
        />
        {selectedFile ? (
          <FileEditor
            path={selectedFile.path}
            content={editorContent}
            onChange={setEditorContent}
            mode={editingFile ? 'editor' : 'preview'}
            readOnly={!editingFile}
            className={`rounded-2xl min-h-[300px] ${treeOpen ? 'h-[calc(100svh-380px)]' : 'h-[calc(100svh-220px)]'}`}
          />
        ) : (
          <div className="flex h-[300px] items-center justify-center text-sm text-muted-foreground">
            Select a file to view.
          </div>
        )}
      </div>
    );
  },
);
